import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildMaxTimeline,
  fetchMaxesHistory,
  formatE1rm,
  formatWeight,
  MAX_TIMELINE_ROW_CAP,
  type MaxHistoryItem,
  type MaxSource,
  type MaxTimeline,
} from "@heraion/shared";

import { Badge, type BadgeTone } from "../primitives/Badge";
import { weightUnitLabels } from "../weightUnitLabels";
import { useAsyncResource } from "../Logger/useAsyncResource";
import { PanelTitleRow, WidgetTitleRow } from "./InfoPill";
import { makeTierTwoStyles } from "./tierTwoPanelStyles";
import { progressWidgetLabel } from "./progressWidgetCatalog";
import { sliceRows, type RowSlice } from "./reportSectionRows";
import { ProgressWidgetStatus } from "./ProgressWidgetStatus";
import type { ProgressApiFetch, ProgressDeps } from "./types";
import { useExerciseNameResolver } from "./useExerciseNameResolver";
import { useSessionResultsFromDeps } from "./useSessionResults";

type FormatDate = (iso: string, locale: string) => string;

// Own the fetch, not deps: `/api/me/maxes?include=history` is exactly the
// per-exercise history read that once froze mobile's Progress tab
// (useProgressResources.ts). Safe here only because a hidden or collapsed
// widget is never mounted (ProgressView, ProgressCollapsibleWidget), so this
// effect fires only while the card is actually on screen. Built on
// useAsyncResource (Logger's SWR hook) rather than a hand-rolled effect, the
// same call shape usePrRecordsResource.ts already makes.
// PG-64: the report reads it once for its screen and its image, and knows
// from it when the image is ready; `enabled: false` leaves it to the owner.
export type MaxHistory = { items: MaxHistoryItem[] | null; error: boolean };
export function useMaxHistory(apiFetch: ProgressApiFetch, enabled = true): MaxHistory {
  const resource = useAsyncResource<MaxHistoryItem[]>({
    key: "progress/prTimeline",
    fetch: () => fetchMaxesHistory(apiFetch),
    immediate: enabled,
  });
  return { items: resource.value, error: resource.status === "error" };
}

// "tested" is the one source that is a confirmed single-rep PR, exactly the
// moment Badge.tsx reserves the gold "pr" tone for; every other source keeps
// the quieter neutral tone. The tone is a REINFORCEMENT, not the distinction
// itself: the badge's label word carries that (below).
const SOURCE_TONE: Record<MaxSource, BadgeTone> = {
  tested: "pr",
  manual: "neutral",
  derived: "neutral",
  calculated_seed: "neutral",
  imported: "neutral",
};

// DESIGN.md Decision 3: a derived/estimated number (Epley projection, the
// seed bootstrap) is coarser than a logged weight and must say so at 1
// decimal via `formatE1rm`, never `formatWeight`'s scale-reading precision.
// tested/manual/imported are all a number someone actually logged or typed.
const ESTIMATED_SOURCES: ReadonlySet<MaxSource> = new Set([
  "derived",
  "calculated_seed",
]);

// PG-59: "PR timeline with provenance". MAXes over time, newest first, each
// row naming WHERE the number came from (max.source, schemas/max.ts):
// tested is a confirmed single-rep PR, everything else (manual guess,
// derived projection, seed bootstrap, import) is not. The provenance word is
// the distinction, never colour alone
// ([[feedback_user_visual_accessibility]]). Off by default
// (PROGRESS_DEFAULT_HIDDEN_WIDGETS); no new endpoint or field, reads the
// existing `/api/me/maxes?include=history`.
export function ProgressPrTimelineCard({
  deps,
  formatDate,
  locale,
  history,
  slice,
}: {
  deps: ProgressDeps;
  formatDate: FormatDate;
  locale: string;
  /** Read by the owner (the report). Absent: the card reads it itself. */
  history?: MaxHistory;
  /** One report page's rows. The cap note goes on the page with the last. */
  slice?: RowSlice;
}) {
  // PG-64: Progress passes no range (the latest MAXes); the report passes
  // its period, «PRs in the period».
  const { theme, apiFetch, units, range } = deps;
  const { t } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const styles = useMemo(() => makeTierTwoStyles(theme), [theme]);
  const own = useMaxHistory(apiFetch, history === undefined);
  const { items, error } = history ?? own;
  const { catalog } = useSessionResultsFromDeps(deps);
  const getName = useExerciseNameResolver(catalog);

  const timeline = useMemo<MaxTimeline | null>(
    () => (items === null ? null : buildMaxTimeline(items, range)),
    [items, range],
  );

  const title = t("mobile.progress.prTimeline.title", {
    defaultValue: "PR timeline",
  });
  const help = range
    ? t("mobile.progress.prTimeline.helpPeriod", {
        defaultValue:
          "The MAXes set in this period, newest first. Tested means a confirmed single-rep PR; every other label is a starting estimate, not a tested max.",
      })
    : t("mobile.progress.prTimeline.help", {
        defaultValue:
          "MAXes over time, newest first. Tested means a confirmed single-rep PR; every other label is a starting estimate, not a tested max.",
      });
  // The unknown source falls back to a translated label, never the raw
  // API slug: an untranslated `source` value would otherwise leak straight
  // to the screen and to the screen reader (rowA11y).
  const unknownSourceLabel = t("mobile.progress.prTimeline.source.unknown", {
    defaultValue: "Unknown",
  });
  const sourceLabel = (source: MaxSource): string =>
    t(`mobile.progress.prTimeline.source.${source}`, {
      defaultValue: unknownSourceLabel,
    });

  const titleRow = (
    <WidgetTitleRow theme={theme} title={progressWidgetLabel(t, "prTimeline")} />
  );

  if (error || timeline === null) {
    return (
      <ProgressWidgetStatus
        theme={theme}
        titleRow={titleRow}
        error={
          error
            ? t("mobile.progress.loadFailed", { defaultValue: "Couldn’t load" })
            : null
        }
        loading={!error && timeline === null}
      />
    );
  }

  const { rows, total } = timeline;

  if (rows.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={title} body={help} />
        <Text style={styles.empty}>
          {range
            ? t("mobile.progress.prTimeline.emptyPeriod", {
                defaultValue: "No MAX set in this period.",
              })
            : t("mobile.progress.prTimeline.empty", {
                defaultValue: "No MAXes recorded yet.",
              })}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <PanelTitleRow theme={theme} title={title} body={help} />
      {sliceRows(rows, slice).map((row, i) => {
        const name = getName(row.exerciseId);
        const value = ESTIMATED_SOURCES.has(row.source)
          ? formatE1rm(row.valueKg, units, locale, unitLabels)
          : formatWeight(row.valueKg, units, locale, unitLabels);
        const date = formatDate(row.setAt, locale);
        const source = sourceLabel(row.source);
        // One accessible node per row, mirroring Badge.tsx: `accessible`
        // groups name+value+date+source into ONE announcement instead of a
        // screen reader stepping through four separate text nodes.
        const rowA11y = t("mobile.progress.prTimeline.rowA11y", {
          name,
          value,
          date,
          source,
          defaultValue: "{{name}}, {{value}}, {{date}}, {{source}}",
        });
        return (
          <View
            key={`${row.exerciseId}#${row.setAt}`}
            style={[styles.timelineRow, i > 0 && styles.timelineRowDivider]}
            accessible
            accessibilityLabel={rowA11y}
          >
            <View style={styles.timelineHeadRow}>
              <Text style={styles.timelineName}>{name}</Text>
              <Text style={styles.timelineValue}>{value}</Text>
            </View>
            <View style={styles.timelineMetaRow}>
              <Text style={styles.timelineDate}>{date}</Text>
              <Badge theme={theme} tone={SOURCE_TONE[row.source]} label={source} />
            </View>
          </View>
        );
      })}
      {total > MAX_TIMELINE_ROW_CAP && (!slice || slice[1] >= rows.length) ? (
        <Text style={styles.timelineTruncated}>
          {t("mobile.progress.prTimeline.truncated", {
            cap: MAX_TIMELINE_ROW_CAP,
            count: total,
            defaultValue: "Showing the last {{cap}} of {{count}} MAXes",
          })}
        </Text>
      ) : null}
    </View>
  );
}
